import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Plus,
  Share2,
  Trash2,
  Check,
  AlertTriangle,
  Lock,
  Globe,
  MoreHorizontal,
  Folder,
} from 'lucide-react';
import {
  Button,
  IconButton,
  Kbd,
  Input,
  Field,
  Badge,
  Tag,
  Wordmark,
  Dialog,
  Menu,
  Tooltip,
  TooltipProvider,
} from '@glob/ui';
import { GlobVector, GlobPixel, GlobAscii, GLOB_MOODS } from '@glob/glob';
import { ThemeToggle } from '../../components/ThemeToggle';
import { useT } from '../../i18n';

// Internal design-system preview. Renders the live token set under the active
// theme so the tokens, type scale, and status colours can be eyeballed on
// staging across dark / light / high-contrast. Not linked from the product
// nav; reachable at /design for review.

interface Swatch {
  token: string;
  cssVar: string;
  role: string;
}

const NEUTRALS: Swatch[] = [
  { token: 'bg-0', cssVar: '--bg-0', role: 'page floor' },
  { token: 'bg-1', cssVar: '--bg-1', role: 'surface' },
  { token: 'bg-2', cssVar: '--bg-2', role: 'card' },
  { token: 'bg-3', cssVar: '--bg-3', role: 'hover · input' },
  { token: 'bg-4', cssVar: '--bg-4', role: 'selected' },
];

const TEXTS: Swatch[] = [
  { token: 'text-1', cssVar: '--text-1', role: 'primary' },
  { token: 'text-2', cssVar: '--text-2', role: 'secondary' },
  { token: 'text-3', cssVar: '--text-3', role: 'meta' },
  { token: 'text-4', cssVar: '--text-4', role: 'placeholder' },
  { token: 'border', cssVar: '--border', role: '1px divider' },
];

const ACCENTS: Swatch[] = [
  { token: 'accent-base', cssVar: '--accent-base', role: 'midnight' },
  { token: 'accent', cssVar: '--accent', role: 'primary CTA' },
  { token: 'accent-hi', cssVar: '--accent-hi', role: 'hover · focus' },
  { token: 'highlight', cssVar: '--highlight', role: 'share · locked · gold' },
];

const STATUS: Swatch[] = [
  { token: 'success', cssVar: '--success', role: 'save confirmed' },
  { token: 'warn', cssVar: '--warn', role: 'soft cap' },
  { token: 'error', cssVar: '--error', role: 'save failed' },
];

const TYPE_SCALE = [
  { px: 48, role: 'display', sample: 'Your Space' },
  { px: 36, role: 'h1', sample: "Save the page you're looking at" },
  { px: 28, role: 'h2', sample: 'How modern crypto works' },
  { px: 22, role: 'h3', sample: 'Recovery passphrase' },
  { px: 18, role: 'body-lg', sample: 'We store ciphertext only. We cannot decrypt your data.' },
  { px: 16, role: 'body', sample: 'Used anywhere prose needs to breathe.' },
  { px: 14, role: 'body-sm', sample: 'Item titles, form labels, menu items.' },
  { px: 12, role: 'caption', sample: 'Meta · 2 hours ago · #reading' },
];

function SwatchRow({ items }: { items: Swatch[] }) {
  return (
    <div className="flex flex-wrap gap-3">
      {items.map((s) => (
        <div key={s.token} className="flex flex-col gap-1.5">
          <div
            className="size-16 rounded-md border border-border"
            style={{ background: `var(${s.cssVar})` }}
          />
          <div className="text-xs font-mono text-text-2">{s.token}</div>
          <div className="text-[11px] text-text-3">{s.role}</div>
        </div>
      ))}
    </div>
  );
}

function DesignSystemPage() {
  const t = useT();
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
    <TooltipProvider>
      <main className="min-h-screen bg-bg-0 text-text-1">
        <div className="max-w-4xl mx-auto p-8 space-y-12">
          <header className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider text-text-3">
                design system · live tokens
              </div>
              <h1 className="text-4xl font-display tracking-tight mt-1">{t('design.title')}</h1>
              <p className="text-text-2 mt-2 max-w-prose">{t('design.subtitle')}</p>
            </div>
            <ThemeToggle />
          </header>

          <section className="space-y-6">
            <h2 className="text-lg font-display">Identity</h2>
            <div className="flex flex-wrap items-center gap-8 p-5 rounded-lg border border-border bg-bg-1">
              <Wordmark variant="lock" size={28} />
              <Wordmark variant="plain" size={28} />
            </div>
          </section>

          <section className="space-y-5">
            <h2 className="text-lg font-display">Glob — the companion</h2>
            <div className="space-y-2">
              <div className="text-sm text-text-3">Vector · nine moods</div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {GLOB_MOODS.map((mood) => (
                  <div
                    key={mood}
                    className="flex flex-col items-center gap-2 p-3 rounded-lg border border-border bg-bg-1"
                  >
                    <GlobVector mood={mood} size={84} />
                    <span className="text-[11px] font-mono text-text-3">{mood}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="text-sm text-text-3">Pixel · 8-bit</div>
                <div className="flex items-center gap-4 p-4 rounded-lg border border-border bg-bg-1">
                  {(['idle', 'happy', 'lock', 'sleep'] as const).map((mood) => (
                    <div key={mood} className="flex flex-col items-center gap-2">
                      <GlobPixel mood={mood} size={56} />
                      <span className="text-[10px] font-mono text-text-3">{mood}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm text-text-3">ASCII · terminal</div>
                <div className="p-4 rounded-lg border border-border bg-bg-1">
                  <GlobAscii variant="idle" size={11} />
                </div>
              </div>
            </div>
            <p className="text-xs font-mono text-text-4">
              In the app, Glob lives in the corner: he sleeps when you step away, shuts his eyes
              when the vault locks, and eats the current page on ⌘D.
            </p>
          </section>

          <section className="space-y-5">
            <h2 className="text-lg font-display">Components</h2>

            <div className="space-y-2">
              <div className="text-sm text-text-3">Button</div>
              <div className="flex flex-wrap items-center gap-3 p-4 rounded-lg border border-border bg-bg-1">
                <Button variant="primary">
                  <Plus size={16} aria-hidden />
                  New Memo
                  <Kbd>⌘N</Kbd>
                </Button>
                <Button variant="secondary">
                  <Share2 size={16} aria-hidden />
                  Share
                </Button>
                <Button variant="ghost">
                  <Folder size={16} aria-hidden />
                  Move to…
                </Button>
                <Button variant="danger">
                  <Trash2 size={16} aria-hidden />
                  Delete
                </Button>
                <Button variant="primary" disabled>
                  Disabled
                </Button>
                <Button variant="primary" size="sm">
                  Small
                </Button>
                <Button variant="secondary" size="lg">
                  Large
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm text-text-3">Icon button · tooltip</div>
              <div className="flex flex-wrap items-center gap-2 p-4 rounded-lg border border-border bg-bg-1">
                <Tooltip content="Share this item">
                  <IconButton aria-label="Share">
                    <Share2 size={18} aria-hidden />
                  </IconButton>
                </Tooltip>
                <IconButton aria-label="More actions" active>
                  <MoreHorizontal size={18} aria-hidden />
                </IconButton>
                <Menu.Root>
                  <Menu.Trigger asChild>
                    <IconButton aria-label="Open menu">
                      <MoreHorizontal size={18} aria-hidden />
                    </IconButton>
                  </Menu.Trigger>
                  <Menu.Content align="start">
                    <Menu.Label>Quick actions</Menu.Label>
                    <Menu.Item>
                      <Plus size={14} aria-hidden />
                      <Menu.Grow>New Memo</Menu.Grow>
                      <Menu.Kbd>⌘N</Menu.Kbd>
                    </Menu.Item>
                    <Menu.Item>
                      <Share2 size={14} aria-hidden />
                      <Menu.Grow>Share</Menu.Grow>
                      <Menu.Kbd>⌘⇧S</Menu.Kbd>
                    </Menu.Item>
                    <Menu.Separator />
                    <Menu.Item danger>
                      <Trash2 size={14} aria-hidden />
                      <Menu.Grow>Delete item</Menu.Grow>
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Root>
                <span className="text-xs font-mono text-text-3 ml-2">
                  hover the share icon · open the ⋯ menu
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm text-text-3">Input · field</div>
              <div className="grid sm:grid-cols-2 gap-5 p-4 rounded-lg border border-border bg-bg-1">
                <Field label="Email" hint="We'll send your invite here. Never shared.">
                  <Input placeholder="you@example.com" defaultValue="founder@glob.sh" />
                </Field>
                <Field
                  label="Recovery passphrase"
                  error="Couldn't match a known passphrase. Check word order."
                >
                  <Input placeholder="24 words separated by spaces" />
                </Field>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm text-text-3">Badge</div>
              <div className="flex flex-wrap items-center gap-3 p-4 rounded-lg border border-border bg-bg-1">
                <Badge variant="success">
                  <Check size={12} aria-hidden />
                  Saved
                </Badge>
                <Badge variant="warn">
                  <AlertTriangle size={12} aria-hidden />
                  Soft cap
                </Badge>
                <Badge variant="error">
                  <AlertTriangle size={12} aria-hidden />
                  Sync failed
                </Badge>
                <Badge variant="locked">
                  <Lock size={12} aria-hidden />
                  Locked
                </Badge>
                <Badge variant="muted">
                  <Globe size={12} aria-hidden />
                  Shared with you
                </Badge>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm text-text-3">Tag · colour is a deterministic hash</div>
              <div className="flex flex-wrap items-center gap-2 p-4 rounded-lg border border-border bg-bg-1">
                {['reading', 'crypto', 'design', 'snippets', 'research', 'urgent', 'archive'].map(
                  (name) => (
                    <Tag key={name} name={name} />
                  ),
                )}
                <Tag name="ai" onRemove={() => undefined} />
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm text-text-3">Dialog</div>
              <div className="flex items-center gap-3 p-4 rounded-lg border border-border bg-bg-1">
                <Dialog.Root open={dialogOpen} onOpenChange={setDialogOpen}>
                  <Dialog.Trigger asChild>
                    <Button variant="secondary">Open dialog</Button>
                  </Dialog.Trigger>
                  <Dialog.Content>
                    <Dialog.Title>Delete this item?</Dialog.Title>
                    <Dialog.Description>
                      You can restore it from Trash within 30 days. After that, it&apos;s gone.
                    </Dialog.Description>
                    <Dialog.Footer>
                      <Dialog.Close asChild>
                        <Button variant="ghost">Cancel</Button>
                      </Dialog.Close>
                      <Button variant="danger">
                        <Trash2 size={16} aria-hidden />
                        Delete
                      </Button>
                    </Dialog.Footer>
                  </Dialog.Content>
                </Dialog.Root>
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <h2 className="text-lg font-display">Colour</h2>
            <div className="space-y-2">
              <div className="text-sm text-text-3">Neutral spine</div>
              <SwatchRow items={NEUTRALS} />
            </div>
            <div className="space-y-2">
              <div className="text-sm text-text-3">Text + border</div>
              <SwatchRow items={TEXTS} />
            </div>
            <div className="space-y-2">
              <div className="text-sm text-text-3">Accent + highlight</div>
              <SwatchRow items={ACCENTS} />
            </div>
            <div className="space-y-2">
              <div className="text-sm text-text-3">Status — always paired with an icon</div>
              <SwatchRow items={STATUS} />
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-display">Type</h2>
            <div className="divide-y divide-border border border-border rounded-lg overflow-hidden">
              {TYPE_SCALE.map((row) => (
                <div key={row.role} className="flex items-baseline gap-4 px-4 py-3">
                  <span className="w-12 text-xs font-mono text-text-3 shrink-0">{row.px}px</span>
                  <span className="w-20 text-xs font-mono text-text-3 shrink-0">{row.role}</span>
                  <span
                    className="truncate"
                    style={{ fontSize: row.px, fontWeight: row.px >= 22 ? 500 : 400 }}
                  >
                    {row.sample}
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-display">Radius</h2>
            <div className="flex gap-4">
              {(['sm', 'md', 'lg', 'xl'] as const).map((r) => (
                <div key={r} className="flex flex-col items-center gap-2">
                  <div
                    className="size-16 bg-bg-2 border border-border-strong"
                    style={{ borderRadius: `var(--radius-${r})` }}
                  />
                  <span className="text-xs font-mono text-text-3">{r}</span>
                </div>
              ))}
            </div>
          </section>

          <footer className="text-xs font-mono text-text-4 pt-8 border-t border-border">
            WCAG 2.2 AA · Inter + JetBrains Mono · tokens + primitives from @glob/ui
          </footer>
        </div>
      </main>
    </TooltipProvider>
  );
}

export const Route = createFileRoute('/_public/design')({
  component: DesignSystemPage,
});
